/**
 * Popover — всплывающий слой (меню/выбор), рендерится в ПОРТАЛ (document.body) с
 * position:fixed по координатам триггера. Портал нужен, чтобы меню не обрезалось
 * overflow'ом узкой строки таблицы (было: видно только кусок, приходилось скроллить).
 *
 * Доступность: закрытие по Esc и клику вне (useDismiss по [anchor, panel]); управляемое
 * open/onOpenChange; триггер получает aria-haspopup/aria-expanded.
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './cx';
import { useDismiss } from './useDismiss';

export type PopoverAlign = 'start' | 'end';

export interface PopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  children: ReactNode;
  align?: PopoverAlign;
  role?: 'menu' | 'dialog' | 'listbox';
  label?: string;
  className?: string;
  panelClassName?: string;
}

interface Pos {
  top: number;
  left: number;
  align: PopoverAlign;
}

export function Popover({
  open,
  onOpenChange,
  trigger,
  children,
  align = 'end',
  role = 'menu',
  label,
  className,
  panelClassName,
}: PopoverProps): JSX.Element {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Pos | null>(null);

  useDismiss({
    enabled: open,
    onDismiss: () => onOpenChange(false),
    refs: [anchorRef, panelRef],
  });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setPos(null);
      return;
    }
    const measure = (): void => {
      if (!anchorRef.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      // end: правый край панели совпадает с правым краем триггера (transform ниже);
      // start: левый край панели у левого края триггера.
      setPos({ top: Math.round(r.bottom + 4), left: Math.round(align === 'end' ? r.right : r.left), align });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, align]);

  return (
    <span className={cx('pw-pop', className)} ref={anchorRef}>
      <span className="pw-pop__trigger" onClick={() => onOpenChange(!open)} aria-haspopup={role} aria-expanded={open}>
        {trigger}
      </span>
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              className={cx('pw-pop__panel', 'pw-pop__panel--portal', panelClassName)}
              role={role}
              aria-label={label}
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                transform: pos.align === 'end' ? 'translateX(-100%)' : undefined,
                zIndex: 1000,
                maxHeight: `calc(100vh - ${pos.top + 8}px)`,
                overflowY: 'auto',
              }}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
